import { DateTime } from 'luxon';

import { Field } from '@hrlogics/react-components/form';

import {
  VerificationRequestStatus,
  VerificationRequestType,
  VerificationRequestWhere,
} from 'src/api';
import { DateRangeField, StringifiedDateRange } from 'src/components/finalForm';
import {
  VERIFICATION_REQUEST_STATUS_OPTIONS,
  VERIFICATION_REQUEST_TYPES,
} from 'src/components/inboxFilter/constants';
import { TableFilter } from 'src/components/table';
import { getVerificationTypeLabel } from 'src/utils/verificationRequest';

export const VerificationRequestsReportFilterKeys = {
  dateCreated: 'dateCreated',
  types: 'types',
  statuses: 'statuses',
} as const;

const VERIFICATION_REQUEST_TYPE_OPTIONS = VERIFICATION_REQUEST_TYPES.map(type => ({
  value: type,
  label: getVerificationTypeLabel(type),
}));

/**
 * Filter definitions rendered above the verification requests report table.
 * Mirrors the inbox filter look (date range + multi-select dropdowns) so the
 * controls feel native to the rest of the app.
 */
export const getVerificationRequestsReportTableFilters = (): TableFilter[] => [
  {
    id: VerificationRequestsReportFilterKeys.dateCreated,
    type: 'custom',
    component: (
      <Field
        key={VerificationRequestsReportFilterKeys.dateCreated}
        label="Creation Date"
        fullWidth
        component={DateRangeField}
        name={VerificationRequestsReportFilterKeys.dateCreated}
      />
    ),
  },
  {
    id: VerificationRequestsReportFilterKeys.types,
    type: 'select',
    fieldProps: {
      label: 'Type',
      multiple: true,
      options: VERIFICATION_REQUEST_TYPE_OPTIONS,
    },
  },
  {
    id: VerificationRequestsReportFilterKeys.statuses,
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: VERIFICATION_REQUEST_STATUS_OPTIONS,
    },
  },
];

type VerificationRequestsReportTableFilter = {
  dateCreated?: StringifiedDateRange | null;
  types?: VerificationRequestType[] | null;
  statuses?: VerificationRequestStatus[] | null;
};

/** Builds the server-side `where` from the applied table filter values. */
export const buildVerificationRequestsReportWhere = (
  tableFilter?: Record<string, unknown>,
): VerificationRequestWhere => {
  if (!tableFilter) return {};

  const { dateCreated, types, statuses } = tableFilter as VerificationRequestsReportTableFilter;

  const where: VerificationRequestWhere = {};

  if (dateCreated) {
    const [fromDate, toDate] = dateCreated;
    if (fromDate || toDate) {
      // The picker stores date-only strings; expand them to local-day boundaries so the last
      // selected day is included and the range matches the user's calendar, not UTC midnight.
      where.createdAt = {
        gte: fromDate
          ? (DateTime.fromISO(fromDate).startOf('day').toISO() ?? undefined)
          : undefined,
        lte: toDate ? (DateTime.fromISO(toDate).endOf('day').toISO() ?? undefined) : undefined,
      };
    }
  }

  if (types && types.length > 0) {
    where.type = { in: types };
  }

  if (statuses && statuses.length > 0) {
    where.status = { in: statuses };
  }

  return where;
};
